// infrastructure/mappers/serviceCodeMapper.js
import { SERVICE_CODE_ORIGIN } from '../../domain/constants/serviceCode';

/**
 * Reads and writes the stored representation of this context's own ServiceCode.
 *
 * IT GAINED A WRITE PATH WITH ORG-AUTHORED CODES, exactly as this file said it would. `toDocument`
 * builds a `custom` document and nothing else: a managed code is still seeded and maintained by us
 * through the admin SDK, and there is no shape here that could produce one. That is not a courtesy —
 * the rules refuse any client write whose `origin` is not `custom`, so a mapper able to build a
 * managed document would only be able to build a rejected one.
 *
 * THE STORED SHAPE IS THE MODEL SHAPE, unusually for a mapper in this context. There is no legacy
 * document to absorb: the collection is net-new and nothing outside this context reads it. So this
 * maps field by field mostly to keep the boundary honest — a stored document that grows a field does
 * not silently reach the domain, and the domain cannot come to depend on one nobody declared.
 *
 * MAPPED FIELD BY FIELD, NEVER SPREAD, on the same terms as its sibling. A spread would carry the
 * Firestore document id, audit stamps and anything a future seed adds straight into the model, and
 * the model would quietly start meaning whatever the seed happened to write.
 *
 * A DOCUMENT WITH AN UNREADABLE `validFrom` IS RETURNED AS IT STANDS, not repaired and not dropped.
 * Resolution excludes it on every date (see serviceCodeValidity), which is the honest outcome — a
 * code with no position in time cannot be billed. Dropping it here instead would hide a mis-seeded
 * record from the very screens that could show somebody it exists.
 */

/**
 * Firestore document → ServiceCode.
 *
 * @param {Object} doc - the stored document, including its `id`
 * @returns {import('../../domain/model/ServiceCode.types').ServiceCode}
 */
export function toServiceCode(doc) {
  return {
    id: doc?.id ?? null,
    code: typeof doc?.code === 'string' ? doc.code.trim() : null,
    codeSystem: doc?.codeSystem ?? null,
    descriptor: doc?.descriptor ?? '',
    unitBasis: doc?.unitBasis ?? null,
    validFrom: doc?.validFrom ?? null,
    // Absent and null mean the same thing — no end known — and both are the ordinary state. A
    // seeded document may simply omit the key rather than write null.
    retiredOn: doc?.retiredOn ?? null,
    origin: doc?.origin ?? null,
    // Absent on every managed document, where it is not an unset field but the statement that the
    // code belongs to no clinic. The domain reads null the same way.
    clinic: doc?.clinic ?? null,
    isDeleted: doc?.isDeleted === true,
  };
} // toServiceCode()

/**
 * Firestore documents → ServiceCode[], in the order given.
 *
 * DOES NOT SORT, deliberately. Display order is a presentation decision (by code string, by
 * descriptor, grouped by system) and belongs to whichever screen is asking; a mapper that imposed
 * one would make every caller that wanted another fight it.
 *
 * @param {Array<Object>} docs
 * @returns {import('../../domain/model/ServiceCode.types').ServiceCode[]}
 */
export function toServiceCodes(docs) {
  return (docs ?? []).map(toServiceCode);
} // toServiceCodes()

/**
 * An org-authored code's document id: the clinic and the code string, and nothing else.
 *
 * THE CLINIC LEADS, and that is what makes the scheme collision-safe across tenants. Two orgs
 * minting `SC-DIRECT` are two different things — neither is governed, so nothing rolls up through
 * either — and a shared id would fuse them into one record whose descriptor and unit basis belong to
 * whoever wrote last. The seed's managed ids lead with `codeSystem` for the same structural reason,
 * and the two schemes cannot collide because no `codeSystem` value is a clinic id.
 *
 * IT INCLUDES `validFrom`, on the same terms as the seed's scheme, because an org CAN hold the same
 * code twice over. A funder that revises what a code covers reuses the string for a new period, and
 * that is two records in disjoint windows — the same revise-in-place shape the catalog has, for the
 * same reason. An id built from the string alone would make the successor overwrite its predecessor
 * and silently rewrite what every past service resolved to.
 *
 * SO A DUPLICATE IS NARROWER THAN "the same string". Only the same code STARTING ON THE SAME DATE is
 * unrepresentable; a second period is legitimate, and the two periods may even overlap — a funder's
 * own dates are not ours to arbitrate. What the screen still has to do is show the user what they
 * already hold under that string, since a second period is sometimes a revision and sometimes a
 * mistake that looks exactly like one — see `findOwnCodesMatching`.
 *
 * DERIVED RATHER THAN GENERATED, which is the whole concurrency guard on the mint. Two people
 * minting the same code in two tabs compute the same id, the create-only rule refuses the loser, and
 * the collision arrives as something a screen can explain instead of a duplicate nobody notices. A
 * query-based check could not do this — both writers would query, both would see nothing, and both
 * would write. No rule can count documents, so the duplicate has to be unrepresentable.
 *
 * UNAMBIGUOUS BECAUSE IT PARSES FROM THE RIGHT, and the two right-hand parts are the constrained
 * ones. `validFrom` is a calendar date and `code` is refused if it carries `_` — see
 * `codeStringRejection`, and the create rule, which enforces the same thing against a client that
 * skips the form. So the last segment is the date, the one before it is the code, and everything
 * left over is the clinic, whatever it contains.
 *
 * NOT THE OTHER WAY AROUND, and this is the part worth reading twice: the guarantee deliberately
 * does NOT rest on clinic ids. `clinics/{id}` takes a create from any signed-in user under any id
 * (HR-4623), and from there a user can self-assign the clinic, drop their own role and re-assign as
 * manager — so a clinic id containing `_` is something an attacker can arrange, not a naming
 * accident. Were the code half unconstrained instead, clinic `x` with code `A_B` and clinic `x_A`
 * with code `B` would derive one id for a date, and whoever minted second would be told they
 * already hold a code they do not, with no way to mint it at all.
 *
 * The constraint therefore sits on the half this context owns. Moving it back onto clinic ids would
 * need a rule two thousand lines away to hold, and would need an unambiguous encoding here plus a
 * migration of every id already written.
 *
 * @param {string} clinicId
 * @param {string} code - already normalized; the raw form would put two ids on one code
 * @param {string} validFrom - 'YYYY-MM-DD'
 * @returns {string}
 */
export function customCodeDocumentId(clinicId, code, validFrom) {
  return `${clinicId}_${code}_${validFrom}`;
} // customCodeDocumentId()

/**
 * An org-authored code → the document that creates it.
 *
 * CREATE ONLY, and it carries every field the code will ever have. There is deliberately no partial
 * form: a code's intrinsic facts are immutable after this write, so the only later changes are
 * `retiredOn` and the tombstone, and both are small enough that the adapter states them inline
 * rather than routing a half-populated model back through here. A mapper that could also build an
 * update would be a mapper that could silently rewrite `unitBasis`.
 *
 * `codeSystem` IS ABSENT RATHER THAN NULL. Firestore treats the two differently in a query — a
 * document missing a field is excluded from a filter on it rather than returned last — and the
 * catalog read filters on `origin`, never on this. Omitting it keeps the document honest about
 * what it does not have; see the constant for why no `local` member exists to hold instead.
 *
 * `origin` AND `isDeleted` ARE WRITTEN, NOT DEFAULTED. The rules require both on create, and an
 * absent `origin` would drop the document out of every read that narrows by it — visible in the
 * collection, invisible to the product, which is the failure mode the seed already guards against.
 *
 * @param {import('../../application/ports/ServiceCodeRepository.port').MintServiceCodeRequest} code
 *   - already normalized and validated by the use case; this writes what it is given
 * @returns {Object} the stored document, without its id
 */
export function toDocument({ clinicId, code, descriptor, unitBasis, validFrom }) {
  return {
    code,
    descriptor,
    unitBasis,
    validFrom,
    // Explicitly null rather than omitted: a code minted today has no end KNOWN, which is a fact,
    // and every reader treats the two the same way. Written so the document says so.
    retiredOn: null,
    origin: SERVICE_CODE_ORIGIN.CUSTOM,
    // Bare `clinic`, not `clinicId` — the Firestore rules helpers read this exact name, which is
    // where the platform spelling and the domain's meet.
    clinic: clinicId,
    isDeleted: false,
  };
} // toDocument()
